import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, of, interval } from 'rxjs';
import { mapTo } from 'rxjs/operators';
import { SettingsService } from '../services/settings.service';

export type NetworkStatus = 'online' | 'offline' | 'slow' | 'restored' | 'retrying';

@Injectable({
  providedIn: 'root'
})
export class NetworkService {
  private statusSubject = new BehaviorSubject<NetworkStatus>('online');
  public status$ = this.statusSubject.asObservable();

  private lastStatus: NetworkStatus = 'online';
  private pendingStatus: NetworkStatus | null = null;
  private waitTimeout: any = null;

  constructor(private settings: SettingsService) {
    if (settings.Environment != 0) {
      this.monitorNetwork();
    }
  }

  public clearRetryingStatus() {
    if (this.statusSubject.value === 'retrying') {
      const restoredStatus = navigator.onLine ? 'online' : 'offline';
      this.statusSubject.next(restoredStatus);
      this.lastStatus = restoredStatus;
    }
  }

  private monitorNetwork() {
    const online$ = fromEvent(window, 'online').pipe(mapTo('online' as NetworkStatus));
    const offline$ = fromEvent(window, 'offline').pipe(mapTo('offline' as NetworkStatus));
    const init$ = of(navigator.onLine ? 'online' : 'offline');

    merge(init$, online$, offline$).subscribe((status: NetworkStatus) => {
      this.updateStatus(status);
      if (status === 'online') this.checkConnectionSpeed();
    });

    interval(15000).subscribe(() => {
      this.checkConnectionSpeed();
    });
  }

  public updateStatus(status: NetworkStatus) {
    const pendingStatuses: NetworkStatus[] = ['offline', 'slow'];
    if (this.statusSubject.value === 'retrying' && status !== 'retrying') {
      return;
    }

    if (pendingStatuses.includes(status)) {
      clearTimeout(this.waitTimeout);
      this.pendingStatus = status;

      const currentStatus = status;
      this.waitTimeout = setTimeout(() => {
        if (this.pendingStatus === currentStatus) {
          this.statusSubject.next(currentStatus);
          this.lastStatus = currentStatus;
          this.pendingStatus = null;
        }
      }, 60000);
    } else {
      clearTimeout(this.waitTimeout);
      this.pendingStatus = null;

      if (status === 'online' && this.lastStatus !== 'online') {
        this.statusSubject.next('restored');
      } else {
        this.statusSubject.next(status);
      }
      this.lastStatus = status;
    }
  }

  private async checkConnectionSpeed() {
    if (!navigator.onLine) {
      this.updateStatus('offline');
      return;
    }

    const connection = (navigator as any).connection;
    let isLikelySlow = false;

    if (connection && connection.effectiveType) {
      const effectiveType = connection.effectiveType;
      isLikelySlow = effectiveType.includes('2g') || effectiveType.includes('3g') || effectiveType.includes('slow');
    } else {
      isLikelySlow = await this.checkNetworkSpeed();
    }

    this.updateStatus(isLikelySlow ? 'slow' : 'online');
  }

  private async checkNetworkSpeed(): Promise<boolean> {
    const start = performance.now();

    try {
      await fetch('https://www.gstatic.com/generate_204', { method: 'GET', cache: 'no-cache' });
      const duration = performance.now() - start;
      return duration > 3000;
    } catch {
      this.updateStatus('offline');
      return false;
    }
  }
}
